import { Context, createContext, useContext } from "react";
import type {
  EnergyStarUtilityUnit,
  EnergyStarWasteUnit,
  UtilityUnit,
} from "../types";

type UnitConfig = {
  dbName: string;
  valinorUnit: UtilityUnit;
  energyStarUnit?: EnergyStarUtilityUnit | EnergyStarWasteUnit;
};
const UNIT_MAPPINGS: Readonly<UnitConfig[]> = [
  {
    dbName: "therms",
    valinorUnit: "thm",
    energyStarUnit: "therms",
  },
  {
    dbName: "kwhs",
    valinorUnit: "kWh",
    energyStarUnit: "kWh (thousand Watt-hours)",
  },
  {
    dbName: "mbtus",
    valinorUnit: "MBTU",
    energyStarUnit: "MBtu (million Btu)",
  },
  {
    dbName: "kbtus",
    valinorUnit: "kBTU",
    energyStarUnit: "kBtu (thousand Btu)",
  },
  {
    dbName: "ccfs",
    valinorUnit: "CCF",
    energyStarUnit: "ccf (hundred cubic feet)",
  },
  {
    dbName: "kcf",
    valinorUnit: "KCF",
    energyStarUnit: "kcf (thousand cubic feet)",
  },
  {
    dbName: "dcf",
    valinorUnit: "DCF",
  },
  {
    dbName: "cf",
    valinorUnit: "CF",
    energyStarUnit: "cf (cubic feet)",
  },
  {
    dbName: "ton_hours",
    valinorUnit: "ton-hour",
    energyStarUnit: "ton hours",
  },
  {
    dbName: "gallons",
    valinorUnit: "gal",
    energyStarUnit: "Gallons (US)",
  },
  {
    dbName: "cGals",
    valinorUnit: "cGal",
    energyStarUnit: "cGal (hundred gallons) (US)",
  },
  {
    dbName: "kGals",
    valinorUnit: "kGal",
    energyStarUnit: "KGal (thousand gallons) (US)",
  },
  {
    dbName: "giga_joules",
    valinorUnit: "GJ",
  },
  {
    dbName: "joules",
    valinorUnit: "J",
  },
  {
    dbName: "cubic_yards",
    valinorUnit: "yd3",
    energyStarUnit: "Cubic yards",
  },
  {
    dbName: "tons",
    valinorUnit: "ton",
    energyStarUnit: "tons",
  },
  {
    dbName: "thousand_pounds_steam",
    valinorUnit: "kLb",
    energyStarUnit: "KLbs. (thousand pounds)",
  },
  {
    dbName: "cubic_meters",
    valinorUnit: "m3",
  },
  {
    dbName: "tonnes",
    valinorUnit: "tonnes",
  },
  {
    dbName: "mcf",
    valinorUnit: "MCF (million cubic feet)",
    energyStarUnit: "MCF(million cubic feet)",
  },
] as const;

function createUnitCipher<
  K extends keyof UnitConfig,
  V extends keyof UnitConfig
>(
  keyPropName: K,
  valuePropName: V
): Record<
  Exclude<UnitConfig[K], undefined | null>,
  Exclude<UnitConfig[V], undefined | null>
> {
  return Object.fromEntries(
    UNIT_MAPPINGS.map((obj) => [obj[keyPropName], obj[valuePropName]]).filter(
      ([key, value]) => key != null && value != null
    )
  );
}

export const inboundUnitCipher: {
  [key: string]: UtilityUnit;
} = createUnitCipher("dbName", "valinorUnit");

export type UnitsContextType = {
  isLoading: boolean;

  // Accepts either unit ID or UtilityUnit (label)
  getConversionFactor: (
    from: number | UtilityUnit,
    to: number | UtilityUnit
  ) => number | undefined;

  unitsMap: Record<
    number,
    {
      id: number;

      // Name stored in the DB (dbName)
      key: string;

      // Label displayed to the user in the UI (valinorUnit)
      label?: UtilityUnit;
    }
  >;

  getUnitIdFromUnit: (label: UtilityUnit) => number;
};

const defaultValue: UnitsContextType = {
  isLoading: true,
  getConversionFactor: () => undefined,
  unitsMap: {},
  getUnitIdFromUnit: () => -1,
};

const UnitsContext: Context<UnitsContextType> = createContext(defaultValue);

export const useUnits = () => {
  return useContext(UnitsContext);
};

export default UnitsContext;
